/**
 * 🎭 PLAN SIMULATOR - Admin Dev Tool
 * 
 * Botão flutuante que permite ao admin simular diferentes planos
 * para testar a experiência do utilizador sem mudar a base de dados.
 * 
 * Visível apenas para utilizadores admin.
 */

import React, { useState, useContext, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '@/App';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { 
  Bug, X, User, Crown, Star, Zap, Shield,
  ChevronUp, ChevronDown, Eye, EyeOff, RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const PLANS = [
  { 
    id: 'free', 
    name: 'FREE', 
    icon: User, 
    color: 'bg-gray-500',
    description: 'Acesso básico'
  },
  { 
    id: 'essential', 
    name: 'ESSENTIAL', 
    icon: Zap, 
    color: 'bg-blue-500',
    description: 'Refeições + Spirit'
  },
  { 
    id: 'premium', 
    name: 'PREMIUM', 
    icon: Star, 
    color: 'bg-purple-500',
    description: 'Challenge 8K + Training'
  },
  { 
    id: 'vip', 
    name: 'VIP', 
    icon: Crown, 
    color: 'bg-amber-500',
    description: 'Acesso total'
  }
];

const TEST_ACCOUNTS = [
  { email: 'free@ketolab.pt', password: 'teste2025', plan: 'FREE' },
  { email: 'essential@ketolab.pt', password: 'teste2025', plan: 'ESSENTIAL' },
  { email: 'premium@ketolab.pt', password: 'teste2025', plan: 'PREMIUM' },
  { email: 'cliente@ketolab.pt', password: 'cliente2025', plan: 'VIP' },
];

const PlanSimulator = () => {
  const { user } = useContext(AuthContext);
  const { currentPlan, setSimulatedPlan, simulatedPlan, clearSimulation } = useSubscription();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showAccounts, setShowAccounts] = useState(false);
  
  // Only show for admin users
  const isAdmin = user && (
    user.access_level === 'ADMIN' || 
    user.role === 'admin' || 
    user.is_admin ||
    user.subscription_plan === 'admin' ||
    user.email === 'admin@ketolab.pt'
  );
  
  if (!isAdmin) return null;
  
  const activePlan = simulatedPlan || currentPlan;
  const isSimulating = !!simulatedPlan;
  
  const handlePlanSelect = (planId) => {
    if (setSimulatedPlan) {
      setSimulatedPlan(planId);
    } else {
      // Fallback: store in localStorage for components to read
      localStorage.setItem('simulatedPlan', planId);
      window.dispatchEvent(new Event('planSimulationChanged'));
    }
  };
  
  const handleClearSimulation = () => {
    if (clearSimulation) {
      clearSimulation();
    } else {
      localStorage.removeItem('simulatedPlan');
      window.dispatchEvent(new Event('planSimulationChanged'));
    }
  };
  
  const copyCredentials = (account) => {
    navigator.clipboard.writeText(`${account.email} / ${account.password}`);
  };

  return (
    <>
      {/* Floating Button - Hidden on mobile (admin use desktop) */}
      <motion.button
        className={`hidden md:flex fixed top-20 right-4 z-[9990] w-10 h-10 rounded-full shadow-lg items-center justify-center transition-colors ${
          isSimulating 
            ? 'bg-orange-500 hover:bg-orange-600' 
            : 'bg-zinc-800 hover:bg-zinc-700 opacity-50 hover:opacity-100'
        }`}
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        title="Plan Simulator (Admin)"
      >
        {isSimulating ? (
          <Eye className="w-4 h-4 text-white" />
        ) : (
          <Bug className="w-5 h-5 text-[#a3e635]" />
        )}
      </motion.button>
      
      {/* Simulation Indicator - Moved to top */}
      {isSimulating && !isOpen && (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="fixed top-20 right-16 z-[9989] bg-orange-500/90 text-white text-xs px-2 py-1 rounded-full shadow-lg"
        >
          VIP
        </motion.div>
      )}
      
      {/* Panel - Moved to top-right */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className="fixed top-32 right-4 z-[9990] w-72 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-3 bg-zinc-800 border-b border-zinc-700">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#a3e635]" />
                <span className="text-sm font-bold text-white">Plan Simulator</span>
              </div>
              <div className="flex items-center gap-1">
                <button 
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1 hover:bg-zinc-700 rounded"
                >
                  {isMinimized ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </button>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-zinc-700 rounded"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </div>
            
            {!isMinimized && (
              <div className="p-3 space-y-3">
                {/* Current Status */}
                <div className="text-xs text-gray-400">
                  <span>Plano real: </span>
                  <Badge variant="outline" className="text-[10px]">
                    {currentPlan?.toUpperCase() || 'N/A'}
                  </Badge>
                  {isSimulating && (
                    <>
                      <span className="mx-1">→</span>
                      <Badge className="bg-orange-500/20 text-orange-400 text-[10px]">
                        {simulatedPlan?.toUpperCase()}
                      </Badge>
                    </>
                  )}
                </div>
                
                {/* Plan Selection */}
                <div className="grid grid-cols-2 gap-2">
                  {PLANS.map((plan) => {
                    const Icon = plan.icon;
                    const isActive = activePlan === plan.id;
                    
                    return (
                      <button
                        key={plan.id}
                        onClick={() => handlePlanSelect(plan.id)}
                        className={`p-2 rounded-lg border transition-all text-left ${
                          isActive 
                            ? 'border-[#a3e635] bg-[#a3e635]/10' 
                            : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-6 h-6 rounded-full ${plan.color} flex items-center justify-center`}>
                            <Icon className="w-3 h-3 text-white" />
                          </div>
                          <span className={`text-xs font-bold ${isActive ? 'text-[#a3e635]' : 'text-white'}`}>
                            {plan.name}
                          </span>
                        </div>
                        <p className="text-[10px] text-gray-500">{plan.description}</p>
                      </button>
                    );
                  })}
                </div>
                
                {/* Clear Simulation */}
                {isSimulating && (
                  <Button
                    onClick={handleClearSimulation}
                    variant="outline"
                    size="sm"
                    className="w-full text-xs border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
                  >
                    <RotateCcw className="w-3 h-3 mr-2" />
                    Limpar Simulação
                  </Button>
                )}
                
                {/* Test Accounts Toggle */}
                <button
                  onClick={() => setShowAccounts(!showAccounts)}
                  className="w-full text-xs text-gray-400 hover:text-white flex items-center justify-center gap-1 py-1"
                >
                  {showAccounts ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {showAccounts ? 'Esconder contas' : 'Ver contas de teste'}
                </button>
                
                {/* Test Accounts List */}
                {showAccounts && (
                  <div className="space-y-1.5 pt-2 border-t border-zinc-700">
                    <p className="text-[10px] text-gray-500 mb-2">
                      Clique para copiar credenciais:
                    </p>
                    {TEST_ACCOUNTS.map((account) => (
                      <button
                        key={account.email}
                        onClick={() => copyCredentials(account)}
                        className="w-full p-2 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors text-left"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-white font-mono">
                            {account.email.split('@')[0]}
                          </span>
                          <Badge className="text-[10px]" variant="outline">
                            {account.plan}
                          </Badge>
                        </div>
                        <p className="text-[10px] text-gray-500 mt-0.5">
                          Pass: {account.password}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Info */}
                <p className="text-[10px] text-gray-600 text-center pt-2 border-t border-zinc-800">
                  💡 Simulação afecta apenas a UI, não as APIs
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PlanSimulator;
